import {
  Children,
  cloneElement,
  FC,
  ReactElement,
  ReactNode,
  useRef,
} from "react";
import { useOverlayTriggerState } from "@react-stately/overlays";
import { useOverlayTrigger } from "@react-aria/overlays";
import { useButton } from "@react-aria/button";
import { LayoutProps, SpaceProps, PositionProps } from "styled-system";

import Content from "./Content";
import Box from "../Box/Box";

type OverlayType = "dialog" | "menu" | "listbox" | "tree" | "grid";
type Placement = "bottom left" | "bottom right";

export interface PopoverProps {
  children: ReactNode;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   The type of the popover<br />
  "dialog" | "menu" | "listbox" | "tree" | "grid"
   */
  type?: OverlayType;
  /**
   Width to be applied
   */
  width?: LayoutProps["width"];
  /**
   Height to be applied
   */
  height?: LayoutProps["height"];
  /**
   Horizontal padding to be applied
   */
  paddingX?: SpaceProps["paddingX"];
  /**
   Vertical padding to be applied
   */
  paddingY?: SpaceProps["paddingY"];
  /**
   Popover container's zIndex
   */
  zIndex?: PositionProps["zIndex"];
  /**
   Additional vertical offset applied to popover container
   */
  offsetY?: SpaceProps["marginTop"];
  /**
   Additional horizontal offset applied to popover container
   */
  offsetX?: PositionProps["left"] | PositionProps["right"];
  /**
   The placement of the popover <br />
    bottom left" | "bottom right"
   */
  placement?: Placement;
}

const Popover: FC<PopoverProps> = ({
  children,
  id,
  type = "dialog",
  width = "auto",
  height = "auto",
  paddingX = 6,
  paddingY = 6,
  zIndex = 1,
  offsetY = 14,
  offsetX = 0,
  placement = "bottom left",
}: PopoverProps) => {
  const [popoverTrigger] = Children.toArray(children);
  const [, popoverContents] = children as ReactNode[];
  const state = useOverlayTriggerState({});

  const ref = useRef<HTMLElement>(null);
  const overlayRef = useRef<HTMLElement>(null);

  const { triggerProps, overlayProps } = useOverlayTrigger(
    { type },
    state,
    ref
  );

  const { buttonProps } = useButton(
    {
      onPress: () => state.toggle(),
    },
    ref
  );

  const triggerElement = cloneElement(popoverTrigger as ReactElement, {
    ...buttonProps,
    ...triggerProps,
    ref,
  });

  const contents =
    typeof popoverContents === "function"
      ? popoverContents(state.close)
      : popoverContents;

  return (
    <Box
      display="inline-block"
      css={`
        position: relative;
      `}
    >
      {triggerElement}
      {state.isOpen && (
        <Content
          {...overlayProps}
          id={id}
          width={width}
          height={height}
          paddingX={paddingX}
          paddingY={paddingY}
          offsetY={offsetY}
          offsetX={offsetX}
          zIndex={zIndex}
          placement={placement}
          data-testid="popover"
          triggerRef={ref}
          ref={overlayRef}
          isOpen={state.isOpen}
          onClose={state.close}
        >
          {contents}
        </Content>
      )}
    </Box>
  );
};

export default Popover;
